import React from 'react';
import { DataGridPro, GridToolbar } from '@mui/x-data-grid-pro';
import { Tooltip } from '@mui/material';
import { Parser } from 'expr-eval';
import { generateColors } from './colors';
import { alignedBox } from './AlignedBox';
import { denormalizeFieldName, formatValue, getMinWidthForField, groupAndCalculate, normalizeFieldName } from './utils';

export const groupDataForGridView = (data, parsedFields) =>
  groupAndCalculate({
    data,
    parsedFields,
    groupKeys: parsedFields.filter(f => f.isDimension).map(f => f.field),
    pivotKey: null,
  });

export const GridView = ({ title, data, fields }) => {
  const barChartInsideField = 'Cost_On_Hand';
  const barChartInsideLabel = 'Cost On Hand';
  let barChartFieldUsed = '';
  const tooltipDisabledFields = [];

  // create columns based on the fields
  const columns = fields.map((parsedField) => {
    const { field, label, format } = parsedField;
    const initialFieldValues = {
      field,
      headerName: label || denormalizeFieldName(field),
      sortable: true,
      minWidth: getMinWidthForField(field, label),
    };

    if (field === barChartInsideField || label === barChartInsideLabel) {
      barChartFieldUsed = field;
      const maxValue = Math.max(...data.map((row) => row[field] || 0));
      return {
        ...initialFieldValues,
        renderCell: (params) => {
          const value = params.value !== 'N/A' ? params.value || 0 : 0;
          const percentage = maxValue > 0 ? (value / maxValue) * 100 : 0;
          return (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                width: '100%',
              }}
            >
              <div
                style={{
                  height: '10px',
                  backgroundColor: generateColors(100)[Math.floor(100 - percentage)],
                  width: `${percentage}%`,
                  borderRadius: '4px',
                }}
              />
              <span style={{ fontSize: '12px', color: '#333' }}>
                {formatValue(value, format)}
              </span>
            </div>
          );
        },
        sortComparator: (v1, v2) => (v1 || 0) - (v2 || 0),
      };
    }

    const possibleRenderCell = !tooltipDisabledFields.includes(field)
      ? {
        renderCell: (params) => (
          <Tooltip title={params.value} placement="top" enterDelay={500}>
            <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {formatValue(params.value, format)}
            </div>
          </Tooltip>
        ),
      } : {};

    return {
      ...initialFieldValues,
      ...possibleRenderCell,
    };
  });

  // map data rows to include only specified fields
  const rows = data.map((row, i) => ({
    id: i,
    ...fields.reduce((acc, f) => {
      acc[f.field] = row[f.field] ?? null;
      return acc;
    }, {}),
  }));

  return alignedBox(title, (
    <DataGridPro
      sx={{
        '& .MuiDataGrid-columnHeaderTitle': {
          whiteSpace: 'normal',
          lineHeight: 'normal'
        },
        '& .MuiDataGrid-columnHeader': {
          height: 'unset !important',
          textAlign: 'left',
        },
        '& .MuiDataGrid-columnHeaders': {
          maxHeight: '168px !important',
          backgroundColor: 'white',
          zIndex: 2
        }
      }}
      rows={rows}
      columns={columns}
      density='compact'
      pageSize={rows.length}
      rowsPerPageOptions={[rows.length]}
      disableSelectionOnClick
      initialState={barChartFieldUsed ? {
        sorting: {
          sortModel: [
            {
              field: barChartFieldUsed,
              sort: 'desc',
            },
          ],
        },
      } : undefined}
      components={{
        Toolbar: GridToolbar
      }}
    />
  ));
};
